import { isEmail, isIP, isMobilePhone, isPort, isURL, isUUID } from '@rxap/validator';
import * as i0 from '@angular/core';
import { Directive, Host, SkipSelf, Input, HostListener, forwardRef, Optional, Self, Inject, InjectionToken, isDevMode, Injector, EventEmitter, ChangeDetectorRef, Output, HostBinding, TemplateRef, ViewContainerRef, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { FormControlName, NG_VALIDATORS, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, FormGroupName, ControlContainer, UntypedFormArray, AbstractControl, UntypedFormGroup, UntypedFormControl, Validators, FormGroupDirective } from '@angular/forms';
import { startWith, filter, tap, map, distinctUntilChanged, debounceTime, take } from 'rxjs/operators';
import { hasIndexSignature, equals, coerceArray, HasRxapOnInitMethod, assertIsArray, assertIsObject, assertIsFunction, isPromise, isObject, clone, coerceBoolean } from '@rxap/utilities';
import { ToggleSubject } from '@rxap/rxjs';
import * as i2 from '@rxap/services';
import { LoadingIndicatorService } from '@rxap/services';
import { merge, defer, of, EMPTY, Subject, isObservable, BehaviorSubject, Subscription } from 'rxjs';
import { GetDefinitionMetadata, DefinitionMetadata } from '@rxap/definition';
import { getMetadata, setMetadataMapSet, setMetadataMap } from '@rxap/reflect-metadata';
import * as i2$1 from '@angular/router';
import { Router } from '@angular/router';
import { ConfirmClick } from '@rxap/directives';

function IsArray({ message } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!Array.isArray(control.value)) {
            return {
                isArray: {
                    expected: 'A array value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsBoolean({ message } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(control.value instanceof Boolean || typeof control.value === 'boolean')) {
            return {
                isBoolean: {
                    expected: 'A boolean value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsString({ message } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(control.value instanceof String || typeof control.value === 'string')) {
            return {
                isString: {
                    expected: 'A string value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

/**
 * Checks if a given value is a number.
 */
function isNumber(value, options = {}) {
    if (typeof value !== 'number') {
        return false;
    }
    if (value === Infinity || value === -Infinity) {
        return options.allowInfinity ?? false;
    }
    if (Number.isNaN(value)) {
        return options.allowNaN ?? false;
    }
    if (options.maxDecimalPlaces !== undefined) {
        let decimalPlaces = 0;
        if (value % 1 !== 0) {
            decimalPlaces = value.toString().split('.')[1].length;
        }
        if (decimalPlaces > options.maxDecimalPlaces) {
            return false;
        }
    }
    return Number.isFinite(value);
}
/**
 * @deprecated use RxapValidators.isNumber() instead
 * @param control
 * @constructor
 */
function IsNumber(control) {
    if (control.value === null) {
        return null;
    }
    if (isNaN(Number(control.value))) {
        return {
            isNumber: {
                expected: 'A number or a string representing a number',
                actual: control.value,
            },
        };
    }
    return null;
}
function _IsNumber({ message, options, } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!isNumber(!options?.strict && typeof control.value === 'string' ? Number(control.value) : control.value, options)) {
            return {
                isNumber: {
                    expected: 'A number value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

const regexOptions = {
    uppercase: '.*[A-Z]',
    special: '.*[^A-Za-z0-9]',
    digit: '.*[0-9]',
    lowercase: '.*[a-z]',
    upperLower: '.*[a-zA-Z]',
    alphaNumeric: '.*[a-zA-Z0-9]',
};
const lengthOptions = {
    min: '.{n,}',
    max: '.{0,n}',
    range: '.{min,max}',
    exact: '.{n}',
    no_limit: '.*',
};
function inOperator(k, o) {
    return k in o;
}
function create(options) {
    let regex = '^';
    for (const [key, value] of Object.entries(regexOptions)) {
        if (inOperator(key, options) && isNumber(options[key])) {
            regex += '(?=' + value.repeat(options[key]) + ')';
        }
    }
    if (isNumber(options.min) && isNumber(options.max)) {
        regex += lengthOptions.range.replace('min', options.min.toFixed(0)).replace('max', options.max.toFixed(0));
    }
    else if (isNumber(options.max)) {
        regex += lengthOptions.max.replace('n', options.max.toFixed(0));
    }
    else if (isNumber(options.min)) {
        regex += lengthOptions.min.replace('n', options.min.toFixed(0));
    }
    else if (isNumber(options.exact)) {
        regex += lengthOptions.exact.replace('n', options.exact.toFixed(0));
    }
    else {
        regex += lengthOptions.no_limit;
    }
    regex += '$';
    return regex;
}
function check(str, regexStringOrOptions) {
    let regexString;
    if (typeof regexStringOrOptions === 'object') {
        regexString = create(regexStringOrOptions);
    }
    else {
        regexString = regexStringOrOptions;
    }
    return new RegExp(regexString).test(str);
}
function checkError(str, options) {
    const returnObject = {};
    for (const [key, value] of Object.entries(options)) {
        returnObject[key] = check(str, { [key]: value });
    }
    return returnObject;
}
function IsComplex({ message, options, }) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        const isNotString = IsString({ message })(control);
        if (isNotString !== null) {
            return isNotString;
        }
        const errors = checkError(control.value, options);
        if (Object.keys(errors).length && Object.values(errors).some(item => !item)) {
            return {
                isComplex: {
                    expected: options,
                    actual: errors,
                    message,
                },
            };
        }
        return null;
    };
}

function IsDate({ message } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(control.value instanceof Date && !isNaN(control.value.getTime()))) {
            return {
                isDate: {
                    expected: 'A date value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsEmail({ message, options, } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(typeof control.value === 'string' && isEmail(control.value, options))) {
            return {
                isEmail: {
                    expected: 'A email value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsEnum({ message, entity, }) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (Object.keys(entity).map(k => entity[k]).indexOf(control.value) === -1) {
            return {
                isEnum: {
                    expected: 'A enum value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsInt({ message, options, } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!isNumber(control.value, options) || Number.isInteger(control.value)) {
            return {
                isInt: {
                    expected: 'A int value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsIP({ message, version, } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(typeof control.value === 'string' && isIP(control.value, version))) {
            return {
                isIp: {
                    expected: `A valid IPv${version ?? '4'} value`,
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsObject({ message } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(control.value !==
            undefined &&
            (typeof control.value === 'object' || typeof control.value === 'function') &&
            !Array.isArray(control.value))) {
            return {
                isObject: {
                    expected: 'A object value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsPhoneNumber({ message, locale, options, } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!isMobilePhone(control.value, locale, options)) {
            return {
                isPhoneNumber: {
                    expected: 'A phone number value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsPort({ message } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(typeof control.value === 'string' && isPort(control.value))) {
            return {
                isPort: {
                    expected: `A valid port number between 1 and 65535`,
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsUrl({ message, options, } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(typeof control.value === 'string' && isURL(control.value, options))) {
            return {
                isURL: {
                    expected: 'A url value',
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

function IsUUID({ message, version = 'all', } = {}) {
    return (control) => {
        if (control.value === null) {
            return null;
        }
        if (!(typeof control.value === 'string' && isUUID(control.value, version))) {
            return {
                isUuid: {
                    expected: version === 'all' ? `A valid uuid` : `A valid uuid version ${version}`,
                    actual: control.value,
                    message,
                },
            };
        }
        return null;
    };
}

const RxapValidators = {
    IsNumber: _IsNumber,
    IsBoolean,
    IsArray,
    IsDate,
    IsEnum,
    IsInt,
    IsObject,
    IsString,
    IsComplex,
    IsPhoneNumber,
    IsEmail,
    IsUrl,
    IsIP,
    IsPort,
    IsUUID,
};

/**
 * @deprecated removed use the rxapControlError or rxapControlErrors directive
 */
class FormControlErrorDirective {
    constructor(template, parent, viewContainerRef) {
        this.template = template;
        this.parent = parent;
        this.viewContainerRef = viewContainerRef;
    }
    ngOnInit() {
        const control = this.parent.control?.get(this.name);
        if (!control) {
            throw new Error('Could not extract form control instance');
        }
        this.subscription = control.statusChanges.pipe(startWith(control.status), filter(status => status === 'INVALID'), tap(() => {
            this.viewContainerRef.clear();
            if (control.hasError(this.errorKey)) {
                this.viewContainerRef.createEmbeddedView(this.template, { $implicit: control.getError(this.errorKey) });
            }
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlErrorDirective, deps: [{ token: i0.TemplateRef }, { token: i1.ControlContainer, host: true, skipSelf: true }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormControlErrorDirective, isStandalone: true, selector: "[rxapFormControlError]", inputs: { name: ["rxapFormControlErrorFrom", "name"], errorKey: ["rxapFormControlErrorIf", "errorKey"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlErrorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormControlError]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i1.ControlContainer, decorators: [{
                    type: Host
                }, {
                    type: SkipSelf
                }] }, { type: i0.ViewContainerRef }], propDecorators: { name: [{
                type: Input,
                args: [{
                        required: true,
                        alias: 'rxapFormControlErrorFrom',
                    }]
            }], errorKey: [{
                type: Input,
                args: [{
                        required: true,
                        alias: 'rxapFormControlErrorIf',
                    }]
            }] } });

class FormControlMarkDirtyDirective {
    constructor(parent) {
        this.parent = parent;
    }
    onClick() {
        const control = this.parent.control;
        if (control && hasIndexSignature(control) && typeof control['markAllAsDirty'] === 'function') {
            control['markAllAsDirty']();
        }
        else {
            control?.markAsDirty();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkDirtyDirective, deps: [{ token: i1.ControlContainer }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormControlMarkDirtyDirective, isStandalone: true, selector: "[rxapFormControlMarkDirty]", host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkDirtyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormControlMarkDirty]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.ControlContainer }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class FormControlMarkPristineDirective {
    constructor(parent) {
        this.parent = parent;
    }
    onClick() {
        const control = this.parent.control;
        if (control && hasIndexSignature(control) && typeof control['markAllAsPristine'] === 'function') {
            control['markAllAsPristine']();
        }
        else {
            control?.markAsPristine();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkPristineDirective, deps: [{ token: i1.ControlContainer }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormControlMarkPristineDirective, isStandalone: true, selector: "[rxapFormControlMarkPristine]", host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkPristineDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormControlMarkPristine]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.ControlContainer }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class FormControlMarkTouchedDirective {
    constructor(parent) {
        this.parent = parent;
    }
    onClick() {
        this.parent.control?.markAllAsTouched();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkTouchedDirective, deps: [{ token: i1.ControlContainer }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormControlMarkTouchedDirective, isStandalone: true, selector: "[rxapFormControlMarkTouched]", host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkTouchedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormControlMarkTouched]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.ControlContainer }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class FormControlMarkUntouchedDirective {
    constructor(parent) {
        this.parent = parent;
    }
    onClick() {
        const control = this.parent.control;
        if (control && hasIndexSignature(control) && typeof control['markAllAsUntouched'] === 'function') {
            control['markAllAsUntouched']();
        }
        else {
            control?.markAsUntouched();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkUntouchedDirective, deps: [{ token: i1.ControlContainer }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormControlMarkUntouchedDirective, isStandalone: true, selector: "[rxapFormControlMarkUntouched]", host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlMarkUntouchedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormControlMarkUntouched]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.ControlContainer }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * A full exertion of FormControlName from @angular/forms. The only change is the
 * ability to access the control container outside of the current component
 *
 * @deprecated use the ParentControlContainerDirective
 */
class FormControlNameDirective extends FormControlName {
    constructor(parent, validators, asyncValidators, valueAccessors) {
        super(parent, validators, asyncValidators, valueAccessors, null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlNameDirective, deps: [{ token: i1.ControlContainer, optional: true, skipSelf: true }, { token: NG_VALIDATORS, optional: true, self: true }, { token: NG_ASYNC_VALIDATORS, optional: true, self: true }, { token: NG_VALUE_ACCESSOR, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormControlNameDirective, isStandalone: true, selector: "[rxapFormControlName]", inputs: { name: ["rxapFormControlName", "name"] }, providers: [
            {
                provide: NgControl,
                useExisting: forwardRef(() => FormControlNameDirective),
            },
        ], exportAs: ["rxapFormControl"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormControlNameDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormControlName]',
                    providers: [
                        {
                            provide: NgControl,
                            useExisting: forwardRef(() => FormControlNameDirective),
                        },
                    ],
                    exportAs: 'rxapFormControl',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.ControlContainer, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: Array, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }, {
                    type: Inject,
                    args: [NG_VALIDATORS]
                }] }, { type: Array, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }, {
                    type: Inject,
                    args: [NG_ASYNC_VALIDATORS]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }, {
                    type: Inject,
                    args: [NG_VALUE_ACCESSOR]
                }] }], propDecorators: { name: [{
                type: Input,
                args: ['rxapFormControlName']
            }] } });

/**
 * A full exertion of FormGroupName from @angular/forms. The only change is the
 * ability to access the control container outside of the current component
 *
 * @deprecated use the ParentControlContainerDirective
 */
class FormGroupNameDirective extends FormGroupName {
    get control() {
        // TODO : add type check
        return this.formDirective.getFormGroup(this);
    }
    constructor(parent, validators, asyncValidators) {
        super(parent, validators, asyncValidators);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormGroupNameDirective, deps: [{ token: i1.ControlContainer, optional: true, skipSelf: true }, { token: NG_VALIDATORS, optional: true, self: true }, { token: NG_ASYNC_VALIDATORS, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormGroupNameDirective, isStandalone: true, selector: "[rxapFormGroupName]", inputs: { name: ["rxapFormGroupName", "name"] }, providers: [
            {
                provide: ControlContainer,
                useExisting: forwardRef(() => FormGroupNameDirective),
            },
        ], exportAs: ["rxapFormGroup"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormGroupNameDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormGroupName]',
                    providers: [
                        {
                            provide: ControlContainer,
                            useExisting: forwardRef(() => FormGroupNameDirective),
                        },
                    ],
                    exportAs: 'rxapFormGroup',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.ControlContainer, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }, {
                    type: Inject,
                    args: [NG_VALIDATORS]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }, {
                    type: Inject,
                    args: [NG_ASYNC_VALIDATORS]
                }] }], propDecorators: { name: [{
                type: Input,
                args: ['rxapFormGroupName']
            }] } });

const RXAP_FORM_DEFINITION = new InjectionToken('rxap/forms/definition');
const RXAP_FORM_DEFINITION_BUILDER = new InjectionToken('rxap/forms/definition-builder');
const RXAP_FORM_SUBMIT_METHOD = new InjectionToken('rxap/form/submit-method');
const RXAP_FORM_SUBMIT_FAILED_METHOD = new InjectionToken('rxap/form/submit-failed-method');
const RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD = new InjectionToken('rxap/form/submit-successful-method');
const RXAP_FORM_LOAD_METHOD = new InjectionToken('rxap/form/load-method');
const RXAP_FORM_LOAD_FAILED_METHOD = new InjectionToken('rxap/form/load-failed-method');
const RXAP_FORM_LOAD_SUCCESSFUL_METHOD = new InjectionToken('rxap/form/load-successful-method');

var MetadataKeys;
(function (MetadataKeys) {
    MetadataKeys["CONTROL_VALIDATORS"] = "rxap/forms/control-validators";
    MetadataKeys["CONTROL_ASYNC_VALIDATORS"] = "rxap/forms/control-async-validators";
    MetadataKeys["CONTROL_CHANGES"] = "rxap/forms/control-changes";
    MetadataKeys["CONTROL_SET_VALUE"] = "rxap/forms/control-set-value";
    MetadataKeys["FORM_ARRAY_GROUPS"] = "rxap/forms/form-array-groups";
    MetadataKeys["FORM_ARRAY_CONTROLS"] = "rxap/forms/form-array-controls";
    MetadataKeys["FORM_GROUPS"] = "rxap/forms/form-groups";
    MetadataKeys["FORM_CONTROLS"] = "rxap/forms/form-controls";
})(MetadataKeys || (MetadataKeys = {}));

function getControlValue(control) {
    if (control.getRawValue) {
        return control.getRawValue();
    }
    return control.value;
}
function controlValueChanges$(control) {
    return merge(defer(() => of(getControlValue(control))), control.valueChanges.pipe(map(() => getControlValue(control))));
}
function controlDisabled$(control) {
    return merge(defer(() => of(control.disabled)), merge(control.statusChanges, control.stateChanges?.asObservable() ?? EMPTY).pipe(map(() => control.disabled), distinctUntilChanged()));
}
function controlEnabled$(control) {
    return merge(defer(() => of(control.enabled)), merge(control.statusChanges, control.stateChanges?.asObservable() ?? EMPTY).pipe(map(() => control.enabled), distinctUntilChanged()));
}
function controlReadonly$(control) {
    return merge(defer(() => of(control.readonly ?? false)), merge(control.statusChanges, control.stateChanges?.asObservable() ?? EMPTY).pipe(map(() => control.readonly ?? false), distinctUntilChanged()));
}
function controlStatusChanges$(control) {
    return merge(defer(() => of(control.status)), control.statusChanges.pipe(map(() => control.status), distinctUntilChanged()));
}
function controlErrorChanges$(control) {
    return merge(defer(() => of(control.errors)), control.valueChanges.pipe(map(() => control.errors)), control.statusChanges.pipe(map(() => control.errors))).pipe(distinctUntilChanged((a, b) => equals(a, b)));
}
function enableControl(control, enabled, opts) {
    if (enabled) {
        control.enable(opts);
    }
    else {
        control.disable(opts);
    }
}
function disableControl(control, disabled, opts) {
    enableControl(control, !disabled, opts);
}
function controlDisabledWhile(control, observable, opts) {
    return observable.subscribe(isDisabled => disableControl(control, isDisabled, opts));
}
function controlEnabledWhile(control, observable, opts) {
    return observable.subscribe(isEnabled => enableControl(control, isEnabled, opts));
}
function mergeControlValidators(control, validators) {
    control.setValidators([...coerceArray(control.validator), ...coerceArray(validators)]);
    control.updateValueAndValidity();
}
function validateControlOn(control, validation) {
    return validation.subscribe(maybeError => {
        control.setErrors(maybeError);
    });
}
function hasErrorAndTouched(control, error, path) {
    const hasError = control.hasError(error, !path || path.length === 0 ? undefined : path);
    return hasError && control.touched;
}
function hasErrorAndDirty(control, error, path) {
    const hasError = control.hasError(error, !path || path.length === 0 ? undefined : path);
    return hasError && control.dirty;
}
function markAllDirty(control) {
    control.markAsDirty({ onlySelf: true });
    control._forEachChild((_control) => _control.markAllAsDirty());
}
function markAllPristine(control) {
    control.markAsPristine({ onlySelf: true });
    control._forEachChild((_control) => _control.markAllAsPristine());
}
function markAllUntouched(control) {
    control.markAsUntouched({ onlySelf: true });
    control._forEachChild((_control) => _control.markAllAsUntouched());
}
function selectControlValue$(control, mapFn) {
    return control.value$.pipe(map(mapFn), distinctUntilChanged());
}

class RxapFormArray extends UntypedFormArray {
    get readonly() {
        return this.parent?.readonly ?? this._readonly;
    }
    set readonly(value) {
        this._readonly = value;
        this.controls.forEach(control => control.stateChanges?.next());
    }
    /**
     * @internal
     */
    get rxapFormDefinition() {
        return this.parent.rxapFormDefinition;
    }
    get controlPath() {
        const parent = this.parent;
        if (parent) {
            if (parent.controlPath) {
                if (parent === this.root) {
                    return this.controlId;
                }
                else {
                    return [parent.controlPath, this.controlId].join('.');
                }
            }
        }
        return this.controlId;
    }
    get fullControlPath() {
        const parent = this.parent;
        if (parent) {
            if (parent.fullControlPath) {
                return [parent.fullControlPath, this.controlId].join('.');
            }
        }
        return this.controlId;
    }
    constructor(controls, options) {
        super(controls, options);
        this.controls = controls;
        this._readonly = false;
        this.value$ = controlValueChanges$(this);
        this.disabled$ = controlDisabled$(this);
        this.enabled$ = controlEnabled$(this);
        this.status$ = controlStatusChanges$(this);
        this.errors$ = controlErrorChanges$(this);
        this.touchChanges = new Subject();
        this.touch$ = this.touchChanges
            .asObservable()
            .pipe(distinctUntilChanged());
        this.dirtyChanges = new Subject();
        this.dirty$ = this.dirtyChanges
            .asObservable()
            .pipe(distinctUntilChanged());
        this.controlId = options.controlId;
        this._builder = options.builder;
        this._controlInsertedFn = options.controlInsertedFn;
        this._controlRemovedFn = options.controlRemovedFn;
    }
    select(mapFn) {
        return this.value$.pipe(map(mapFn), distinctUntilChanged());
    }
    at(index) {
        return super.at(index);
    }
    getRawValue() {
        return super.getRawValue();
    }
    insert(index, control) {
        if (isDevMode()) {
            console.warn('It is not recommend to use the FormArray.insert method');
        }
        return super.insert(index, control);
    }
    /**
     * inserts a new control at the specified index. If the index is undefined
     * the new control will be added to the end.
     *
     * @param index (optional) the index where the control should be created
     * @param state (optional) the initial state of the new control
     * @param options (optional) ControlEventOptions
     */
    insertAt(index, state, options) {
        const insertIndex = index ?? this.controls.length;
        const controlOrDefinition = this._builder(state, {
            controlId: insertIndex.toFixed(0),
        });
        this._controlInsertedFn(insertIndex, controlOrDefinition);
        if (insertIndex < this.controls.length) {
            // update the control ids for all controls, that are moved.
            for (let i = insertIndex; i < this.controls.length; i++) {
                Reflect.set(this.controls[i], 'controlId', (i + 1).toFixed(0));
            }
        }
        // call the super insert after the update, bc the insert method will
        // trigger a change detection
        if (controlOrDefinition instanceof AbstractControl) {
            super.insert(insertIndex, controlOrDefinition, options);
        }
        else {
            super.insert(insertIndex, controlOrDefinition.rxapFormGroup, options);
        }
    }
    disabledWhile(observable, options) {
        return controlDisabledWhile(this, observable, options);
    }
    enabledWhile(observable, options) {
        return controlEnabledWhile(this, observable, options);
    }
    mergeValidators(validators) {
        mergeControlValidators(this, validators);
    }
    mergeAsyncValidators(validators) {
        this.setAsyncValidators([
            ...coerceArray(this.asyncValidator),
            ...coerceArray(validators),
        ]);
        this.updateValueAndValidity();
    }
    markAllAsDirty() {
        markAllDirty(this);
    }
    markAllAsPristine() {
        markAllPristine(this);
    }
    setValue(valueOrObservable, options) {
        if (isObservable(valueOrObservable)) {
            return valueOrObservable.subscribe((value) => {
                super.setValue(value, options);
            });
        }
        super.setValue(valueOrObservable, options);
    }
    markAllAsUntouched() {
        markAllUntouched(this);
    }
    validateOn(observableValidation) {
        return observableValidation.subscribe((maybeError) => {
            this.setErrors(maybeError);
        });
    }
    hasErrorAndTouched(errorCode, path) {
        return hasErrorAndTouched(this, errorCode, path);
    }
    _patchValue(value, options) {
        // Even though the `value` argument type doesn't allow `null` and `undefined` values, the
        // `patchValue` can be called recursively and inner data structures might have these values, so
        // we just ignore such cases when a field containing FormArray instance receives `null` or
        // `undefined` as a value.
        if (value == null /* both `null` and `undefined` */) {
            return;
        }
        if (options?.strict) {
            if (this.length > value.length) {
                for (let index = this.length - 1; index >= value.length; index--) {
                    this.removeAt(index);
                }
            }
        }
        value.forEach((newValue, index) => {
            if (this.at(index)) {
                this.at(index)
                    .patchValue(newValue, {
                    ...(options ?? {}),
                    onlySelf: true,
                });
            }
            else if (options?.coerce) {
                this.insertAt(index, value[index]);
            }
        });
        this.updateValueAndValidity(options);
    }
    patchValue(valueOrObservable, options) {
        if (isObservable(valueOrObservable)) {
            return valueOrObservable.subscribe((value) => {
                this._patchValue(value, options);
            });
        }
        this._patchValue(valueOrObservable, options);
    }
    hasErrorAndDirty(errorCode, path) {
        return hasErrorAndDirty(this, errorCode, path);
    }
    removeAt(index) {
        if (isDevMode()) {
            console.warn('It is not recommend to use the FormArray.removeAt method');
        }
        super.removeAt(index);
        this._controlRemovedFn(index);
    }
    setEnable(enable = true, opts) {
        enableControl(this, enable, opts);
    }
    push(control, options) {
        if (isDevMode()) {
            console.warn('It is not recommend to use the FormArray.push method');
        }
        return super.push(control, options);
    }
    setDisable(disable = true, opts) {
        disableControl(this, disable, opts);
    }
    setControl(index, control, options) {
        if (isDevMode()) {
            console.warn('It is not recommend to use the FormArray.setControl method');
        }
        return super.setControl(index, control, options);
    }
    markAsTouched(opts) {
        super.markAsTouched(opts);
        this.touchChanges.next(true);
    }
    markAsUntouched(opts) {
        super.markAsUntouched(opts);
        this.touchChanges.next(false);
    }
    markAsPristine(opts) {
        super.markAsPristine(opts);
        this.dirtyChanges.next(false);
    }
    markAsDirty(opts) {
        super.markAsDirty(opts);
        this.dirtyChanges.next(true);
    }
    reset(value, options) {
        super.reset(value, options);
    }
    setValidators(newValidator, updateValueAndValidity = true) {
        super.setValidators(newValidator);
        if (updateValueAndValidity) {
            super.updateValueAndValidity();
        }
    }
    setAsyncValidators(newValidator, updateValueAndValidity = true) {
        super.setAsyncValidators(newValidator);
        if (updateValueAndValidity) {
            super.updateValueAndValidity();
        }
    }
    hasError(errorCode, path) {
        return super.hasError(errorCode, path);
    }
    setErrors(errors, opts = {}) {
        return super.setErrors(errors, opts);
    }
    getError(errorCode, path) {
        return super.getError(errorCode, path);
    }
}

class RxapFormGroup extends UntypedFormGroup {
    /**
     * @internal
     */
    get rxapFormDefinition() {
        if (!this.parent) {
            return this._rxapFormDefinition;
        }
        if (this._rxapFormDefinition) {
            return this._rxapFormDefinition;
        }
        return this.parent.rxapFormDefinition;
    }
    get readonly() {
        return this.parent?.readonly ?? this._readonly;
    }
    set readonly(value) {
        this._readonly = value;
        Object.values(this.controls ?? {}).forEach(control => control.stateChanges?.next());
    }
    get controlPath() {
        const parent = this.parent;
        if (parent) {
            if (parent.controlPath) {
                if (parent === this.root) {
                    return this.controlId;
                }
                else {
                    return [parent.controlPath, this.controlId].join('.');
                }
            }
        }
        return '';
    }
    get fullControlPath() {
        const parent = this.parent;
        if (parent) {
            if (parent.fullControlPath) {
                return [parent.fullControlPath, this.controlId].join('.');
            }
        }
        return this.controlId;
    }
    constructor(controls, options) {
        super(controls, options);
        this.controls = controls;
        this._readonly = false;
        this.touchChanges = new Subject();
        this.dirtyChanges = new Subject();
        this.touch$ = this.touchChanges.asObservable().pipe(distinctUntilChanged());
        this.dirty$ = this.dirtyChanges.asObservable().pipe(distinctUntilChanged());
        this.value$ = controlValueChanges$(this);
        this.disabled$ = controlDisabled$(this);
        this.enabled$ = controlEnabled$(this);
        this.status$ = controlStatusChanges$(this);
        this.errors$ = controlErrorChanges$(this);
        this.controlId = options.controlId;
    }
    select(mapFn) {
        return selectControlValue$(this, mapFn);
    }
    getRawValue() {
        return super.getRawValue();
    }
    get(path) {
        return super.get(path);
    }
    getControl(...names) {
        return this.get(names.join('.'));
    }
    addControl(name, control) {
        super.addControl(name, control);
    }
    removeControl(name) {
        super.removeControl(name);
    }
    contains(controlName) {
        return super.contains(controlName);
    }
    setControl(name, control) {
        super.setControl(name, control);
    }
    setValue(valueOrObservable, options) {
        if (isObservable(valueOrObservable)) {
            return valueOrObservable.subscribe((value) => 
            // TODO : refactor RxapFormGroup to typed FormGroup
            super.setValue(value, options));
        }
        super.setValue(valueOrObservable, options);
    }
    _patchValue(value, options) {
        // Even though the `value` argument type doesn't allow `null` and `undefined` values, the
        // `patchValue` can be called recursively and inner data structures might have these values, so
        // we just ignore such cases when a field containing FormGroup instance receives `null` or
        // `undefined` as a value.
        if (value == null /* both `null` and `undefined` */) {
            return;
        }
        Object.keys(value).forEach(name => {
            // TODO : resolve type issue
            const controls = this.controls;
            if (controls[name]) {
                controls[name].patchValue(value[name], {
                    ...(options ?? {}),
                    onlySelf: true,
                });
            }
        });
        this.updateValueAndValidity(options);
    }
    patchValue(valueOrObservable, options) {
        if (isObservable(valueOrObservable)) {
            return valueOrObservable.subscribe((value) => 
            // TODO : refactor RxapFormGroup to typed FormGroup
            super.patchValue(value, options));
        }
        this._patchValue(valueOrObservable, options);
    }
    disabledWhile(observable, options) {
        return controlDisabledWhile(this, observable, options);
    }
    enabledWhile(observable, options) {
        return controlEnabledWhile(this, observable, options);
    }
    mergeValidators(validators) {
        mergeControlValidators(this, validators);
    }
    mergeAsyncValidators(validators) {
        this.setAsyncValidators([
            ...coerceArray(this.asyncValidator),
            ...coerceArray(validators),
        ]);
        this.updateValueAndValidity();
    }
    markAsTouched(opts) {
        super.markAsTouched(opts);
        this.touchChanges.next(true);
    }
    markAsUntouched(opts) {
        super.markAsUntouched(opts);
        this.touchChanges.next(false);
    }
    markAsPristine(opts) {
        super.markAsPristine(opts);
        this.dirtyChanges.next(false);
    }
    markAsDirty(opts) {
        super.markAsDirty(opts);
        this.dirtyChanges.next(true);
    }
    markAllAsDirty() {
        markAllDirty(this);
    }
    markAllAsPristine() {
        markAllPristine(this);
    }
    markAllAsUntouched() {
        markAllUntouched(this);
    }
    reset(formState, options) {
        super.reset(formState, options);
    }
    setValidators(newValidator, updateValueAndValidity = true) {
        super.setValidators(newValidator);
        if (updateValueAndValidity) {
            super.updateValueAndValidity();
        }
    }
    setAsyncValidators(newValidator, updateValueAndValidity = true) {
        super.setAsyncValidators(newValidator);
        if (updateValueAndValidity) {
            super.updateValueAndValidity();
        }
    }
    validateOn(observableValidation) {
        return validateControlOn(this, observableValidation);
    }
    hasError(errorCode, path) {
        return super.hasError(errorCode, path);
    }
    setErrors(errors, opts = {}) {
        return super.setErrors(errors, opts);
    }
    getError(errorCode, path) {
        return super.getError(errorCode, path);
    }
    hasErrorAndTouched(error, ...path) {
        return hasErrorAndTouched(this, error, ...path);
    }
    hasErrorAndDirty(error, ...path) {
        return hasErrorAndDirty(this, error, ...path);
    }
    setEnable(enable = true, opts) {
        enableControl(this, enable, opts);
    }
    setDisable(disable = true, opts) {
        disableControl(this, disable, opts);
    }
}

class RxapFormControl extends UntypedFormControl {
    /**
     * @internal
     */
    get rxapFormDefinition() {
        return this.parent.rxapFormDefinition;
    }
    get readonly() {
        return this.parent?.readonly ?? this._readonly;
    }
    set readonly(value) {
        this._readonly = value;
        this.stateChanges.next();
    }
    get controlPath() {
        const parent = this.parent;
        if (parent) {
            if (parent.controlPath) {
                if (parent === this.root) {
                    return this.controlId;
                }
                else {
                    return [parent.controlPath, this.controlId].join('.');
                }
            }
        }
        return this.controlId;
    }
    get fullControlPath() {
        const parent = this.parent;
        if (parent) {
            if (parent.fullControlPath) {
                return [parent.fullControlPath, this.controlId].join('.');
            }
        }
        return this.controlId;
    }
    constructor(formState, options) {
        super(formState, options);
        this._readonly = false;
        this.touchChanges = new Subject();
        this.dirtyChanges = new Subject();
        this.touch$ = this.touchChanges
            .asObservable()
            .pipe(distinctUntilChanged());
        this.dirty$ = this.dirtyChanges
            .asObservable()
            .pipe(distinctUntilChanged());
        this.value$ = controlValueChanges$(this);
        this.disabled$ = controlDisabled$(this);
        this.enabled$ = controlEnabled$(this);
        this.status$ = controlStatusChanges$(this);
        this.errors$ = controlErrorChanges$(this);
        this._onSetValue = [];
        this.stateChanges = new Subject();
        this.readonly$ = controlReadonly$(this);
        this.controlId = options.controlId;
        if (options.disabled) {
            this.disable({ emitEvent: false });
        }
        if (options.readonly) {
            this.readonly = true;
        }
        this.initialState = formState;
    }
    setValue(valueOrObservable, options) {
        if (isObservable(valueOrObservable)) {
            return valueOrObservable.subscribe((value) => {
                super.setValue(value, options);
                this._onSetValue.forEach((setValueFn) => setValueFn(value, options));
            });
        }
        super.setValue(valueOrObservable, options);
        this._onSetValue.forEach((setValueFn) => setValueFn(valueOrObservable, options));
    }
    patchValue(valueOrObservable, options) {
        if (isObservable(valueOrObservable)) {
            return valueOrObservable.subscribe((value) => super.patchValue(value, options));
        }
        super.patchValue(valueOrObservable, options);
    }
    disabledWhile(observable, options) {
        return controlDisabledWhile(this, observable, options);
    }
    enabledWhile(observable, options) {
        return controlEnabledWhile(this, observable, options);
    }
    mergeValidators(validators) {
        mergeControlValidators(this, validators);
    }
    mergeAsyncValidators(validators) {
        this.setAsyncValidators([
            // TODO : remove 'as any' if solution for the type overwrite issue is found (above)
            this.asyncValidator,
            ...coerceArray(validators),
        ]);
        this.updateValueAndValidity();
    }
    markAsTouched(opts) {
        super.markAsTouched(opts);
        this.touchChanges.next(true);
    }
    markAsUntouched(opts) {
        super.markAsUntouched(opts);
        this.touchChanges.next(false);
    }
    markAsPristine(opts) {
        super.markAsPristine(opts);
        this.dirtyChanges.next(false);
    }
    markAsDirty(opts) {
        super.markAsDirty(opts);
        this.dirtyChanges.next(true);
    }
    markAllAsDirty() {
        this.markAsDirty({ onlySelf: true });
    }
    reset(formState, options) {
        const newState = formState ?? this.initialState;
        if (typeof newState === 'function') {
            super.reset(newState(), options);
        }
        else {
            super.reset(newState, options);
        }
    }
    setValidators(newValidator, updateValueAndValidity = true) {
        super.setValidators(newValidator);
        if (updateValueAndValidity) {
            super.updateValueAndValidity();
        }
    }
    setAsyncValidators(newValidator, updateValueAndValidity = true) {
        super.setAsyncValidators(newValidator);
        if (updateValueAndValidity) {
            super.updateValueAndValidity();
        }
    }
    validateOn(observableValidation) {
        return validateControlOn(this, observableValidation);
    }
    getError(errorCode) {
        return super.getError(errorCode);
    }
    hasError(errorCode) {
        return super.hasError(errorCode);
    }
    setErrors(errors, opts = {}) {
        return super.setErrors(errors, opts);
    }
    setError(key, value, opts = {}) {
        super.setErrors({ [key]: value }, opts);
    }
    hasErrorAndTouched(error) {
        return hasErrorAndTouched(this, error);
    }
    hasErrorAndDirty(error) {
        return hasErrorAndDirty(this, error);
    }
    setEnable(enable = true, opts) {
        enableControl(this, enable, opts);
    }
    setDisable(disable = true, opts) {
        disableControl(this, disable, opts);
    }
    registerOnSetValue(setValueFn) {
        this._onSetValue.push(setValueFn);
    }
}

/* eslint-disable @typescript-eslint/ban-types */
class FormArrayControlManager {
    constructor(form, controlId) {
        this.form = form;
        this.controlId = controlId;
    }
    inserted(index, controlOrDefinition) {
        const controlContainer = this.form[this.controlId];
        if (Array.isArray(controlContainer)) {
            controlContainer.splice(index, 0, controlOrDefinition);
        }
        else {
            throw new Error('Could not register inserted control or definition. The control id does not points to an array of control or definition instance!');
        }
    }
    removed(index) {
        const controlContainer = this.form[this.controlId];
        if (Array.isArray(controlContainer)) {
            controlContainer.splice(index, 1);
        }
        else {
            throw new Error('Could not register inserted control or definition. The control id does not points to an array of control or definition instance!');
        }
    }
}
class RxapFormBuilder {
    constructor(definition, injector = Injector.NULL, providers = []) {
        this.definition = definition;
        this.injector = injector;
        this.formArrayGroups = this.extractArrayGroups();
        this.formArrayControls = this.extractArrayControls();
        this.formControls = this.extractControls();
        this.formGroups = this.extractGroups();
        this.validators = this.extractValidators();
        this.asyncValidators = this.extractAsyncValidators();
        this.controlChanges = this.extractControlChanges();
        this.controlSetValue = this.extractControlSetValue();
        const formOptions = (this.formOptions = this.extractFormOptions());
        // merge the providers from the from options with the providers from the
        // constructor. If the provider is defined in the form options and constructor
        // then the provider from the constructor will be used.
        this.providers = [...providers, ...(formOptions.providers ?? [])].filter((provider, index, self) => provider['provide'] &&
            self.findIndex((selfProvider) => selfProvider.provide &&
                selfProvider.provide === provider.provide) === index);
    }
    build(state = {}, options = {}) {
        const injector = Injector.create({
            name: `rxap/form-builder/${this.formOptions.id}`,
            parent: this.injector,
            providers: this.providers,
        });
        let form;
        // don't use the notFoundValue feature of the injector.
        // if used for each call of the get method an "empty" or "fallback"
        // instance is created
        try {
            form = injector.get(this.definition);
        }
        catch (e) {
            if (e.name === 'NullInjectorError') {
                if (e.message.includes(`No provider for ${this.definition.name}`)) {
                    if (isDevMode()) {
                        console.warn(`Could not inject the definition instance. Fallback and call the constructor of the definition class: ${e.message}`);
                    }
                    form = new this.definition();
                }
                else {
                    throw e;
                }
            }
            else {
                throw new Error(`Could not inject the definition instance: ${e.message}`);
            }
        }
        const controls = {};
        this.buildControls(state, form, controls);
        this.buildGroups(state, form, controls);
        this.buildArrayGroups(state, form, controls);
        this.buildArrayControls(state, form, controls);
        const formOptions = {
            ...this.formOptions,
            controlId: options.controlId ?? (options.id ? options.id : this.formOptions.id),
            ...options,
        };
        form.rxapFormGroup = new RxapFormGroup(controls, formOptions);
        Reflect.set(form.rxapFormGroup, '_rxapFormDefinition', form);
        form.rxapMetadata = this.formOptions;
        if (HasRxapOnInitMethod(form)) {
            form.rxapOnInit();
        }
        for (const control of Object.values(form.rxapFormGroup.controls)) {
            if (control.controlId) {
                const controlSetValueOnInit = Array.from(this.controlSetValue.get(control.controlId) ?? []).filter((controlSetValueOptions) => controlSetValueOptions.initial);
                for (const onSetValue of this.coerceToFnArray(form, controlSetValueOnInit)) {
                    onSetValue(control.value, { initial: true });
                }
            }
        }
        return form;
    }
    buildArrayControls(builderFormState, form, controls) {
        for (const [controlId, options] of this.formArrayControls.entries()) {
            const formState = this.coerceToArrayFormState(controlId, [], builderFormState, options.state);
            assertIsArray(formState);
            const formControls = formState.map((fs, index) => new RxapFormControl(fs, { controlId: index.toFixed(0) }));
            const manager = new FormArrayControlManager(form, controlId);
            const formArray = (formControls.rxapFormArray =
                controls[controlId] =
                    new RxapFormArray(
                    // TODO : add the formControlOptions property. To definition the form control options
                    formControls, {
                        ...options,
                        controlId,
                        validators: [],
                        asyncValidators: [],
                        controlInsertedFn: manager.inserted.bind(manager),
                        controlRemovedFn: manager.removed.bind(manager),
                        builder: (state, controlOptions) => new RxapFormControl(state, controlOptions),
                    }));
            // TODO : add support for injectable validators
            formArray.setValidators(this.coerceToValidatorArray(form, options.validators, this.validators.get(controlId)), false);
            formArray.setAsyncValidators(this.coerceToValidatorArray(form, options.asyncValidators, this.asyncValidators.get(controlId)), false);
            Reflect.set(form, controlId, formControls);
        }
    }
    buildArrayGroups(builderFormState, form, controls) {
        for (const [controlId, options] of this.formArrayGroups.entries()) {
            const formState = this.coerceToArrayFormState(controlId, [], builderFormState, options.state);
            assertIsArray(formState);
            const formGroupBuilder = new RxapFormBuilder(options.definition, this.injector, this.providers);
            // TODO : add the formGroupOptions property. To definition of the form group options
            const formGroupDefinitions = formState.map((fs, index) => formGroupBuilder.build(fs, { id: index.toFixed(0) }));
            const manager = new FormArrayControlManager(form, controlId);
            const formArray = (formGroupDefinitions.rxapFormArray =
                controls[controlId] =
                    new RxapFormArray(formGroupDefinitions.map((fgd) => fgd.rxapFormGroup), {
                        ...options,
                        builder: formGroupBuilder.build.bind(formGroupBuilder),
                        controlId,
                        validators: [],
                        asyncValidators: [],
                        controlInsertedFn: manager.inserted.bind(manager),
                        controlRemovedFn: manager.removed.bind(manager),
                    }));
            // TODO : add support for injectable validators
            formArray.setValidators(this.coerceToValidatorArray(form, options.validators, this.validators.get(controlId)), false);
            formArray.setAsyncValidators(this.coerceToValidatorArray(form, options.asyncValidators, this.asyncValidators.get(controlId)), false);
            Reflect.set(form, controlId, formGroupDefinitions);
        }
    }
    buildGroups(builderFormState, form, controls) {
        for (const [controlId, options] of this.formGroups.entries()) {
            const formState = this.coerceToGroupFormState(controlId, {}, builderFormState, options.state);
            assertIsObject(formState);
            const formGroupDefinition = new RxapFormBuilder(options.definition, this.injector, this.providers).build(formState, {
                ...options,
                validators: [],
                asyncValidators: [],
            });
            controls[controlId] = formGroupDefinition.rxapFormGroup;
            const formGroup = formGroupDefinition.rxapFormGroup;
            // TODO : add support for injectable validators
            formGroup.setValidators(this.coerceToValidatorArray(form, options.validators, this.validators.get(controlId)), false);
            formGroup.setAsyncValidators(this.coerceToValidatorArray(form, options.asyncValidators, this.asyncValidators.get(controlId)), false);
            // set the form group definition instance to the form definition instance
            Reflect.set(form, controlId, formGroupDefinition);
        }
    }
    buildControls(builderFormState, form, controls) {
        for (const [controlId, options] of this.formControls.entries()) {
            const control = (controls[controlId] = new (options.controlType ??
                RxapFormControl)(this.coerceToControlFormState(controlId, null, builderFormState, options.state), {
                ...options,
                controlId,
                validators: [],
                asyncValidators: [],
            }));
            const injectValidators = [];
            const injectAsyncValidators = [];
            for (const injectValidator of options.injectValidators ?? []) {
                const injectableValidator = this.injector.get(injectValidator);
                if (injectableValidator.validate) {
                    injectValidators.push(injectableValidator.validate.bind(injectableValidator));
                }
                if (injectableValidator.asyncValidate) {
                    injectAsyncValidators.push(injectableValidator.asyncValidate.bind(injectableValidator));
                }
            }
            control.setValidators(this.coerceToValidatorArray(form, options.validators, this.validators.get(controlId), injectValidators), false);
            control.setAsyncValidators(this.coerceToValidatorArray(form, options.asyncValidators, this.asyncValidators.get(controlId), injectAsyncValidators), false);
            // register all control on change function with the form control
            this.coerceToFnArray(form, this.controlChanges.get(controlId)).forEach((changeFn) => control.registerOnChange(changeFn));
            // register all control on set value function with the form control
            this.coerceToFnArray(form, this.controlSetValue.get(controlId)).forEach((setValueFn) => control.registerOnSetValue(setValueFn));
            if (Array.isArray(options.validators)) {
                if (options.validators.includes(Validators.required) ||
                    options.validators.includes(Validators.requiredTrue)) {
                    Reflect.set(control, 'hasRequiredValidator', true);
                }
            }
            // set the form control instance to the form definition instance
            Reflect.set(form, controlId, control);
        }
    }
    /**
     * Coerce to a function array.
     *
     * @param form the current form definition instance
     * @param methodKeys A set of propertyKeys that points to form
     * definition instance methods
     */
    coerceToFnArray(form, methodKeys) {
        const changes = [];
        if (methodKeys) {
            for (const propertyKeyOrOptions of methodKeys) {
                const propertyKey = typeof propertyKeyOrOptions === 'string'
                    ? propertyKeyOrOptions
                    : propertyKeyOrOptions.propertyKey;
                assertIsFunction(form[propertyKey]);
                changes.push(form[propertyKey].bind(form));
            }
        }
        return changes;
    }
    /**
     * Coerce to a validator function array.
     *
     * @param form the current form definition instance
     * @param optionsValidators The options validator functions
     * @param validatorMethodKeys A set of propertyKeys that points to form
     * definition instance methods
     * @param injectValidators Injected validator functions
     */
    coerceToValidatorArray(form, optionsValidators, validatorMethodKeys, injectValidators) {
        const validators = (optionsValidators ? Array.isArray(optionsValidators) ? optionsValidators : [optionsValidators] : []).slice();
        if (validatorMethodKeys) {
            validatorMethodKeys.forEach((propertyKey) => {
                assertIsFunction(form[propertyKey]);
                validators.push(form[propertyKey].bind(form));
            });
        }
        if (injectValidators) {
            validators.push(...injectValidators);
        }
        return validators;
    }
    /**
     * Coerce to the form state for the specified controlId. If none form state is
     * found the default form state will be returned.
     *
     * @param controlId A control id
     * @param defaultFormState The default form state
     * @param builderFormState The form state provides as build state parameter
     * @param optionsFormState The form state set by the decorator in the form definition
     */
    coerceToControlFormState(controlId, defaultFormState, builderFormState, optionsFormState) {
        let formState = defaultFormState;
        if (builderFormState && builderFormState[controlId] !== undefined) {
            formState = builderFormState[controlId];
        }
        else if (optionsFormState !== undefined) {
            formState = optionsFormState ?? null;
        }
        if (typeof formState === 'function') {
            formState = formState();
        }
        return formState;
    }
    /**
     * Coerce to the form state for the specified controlId. If none form state is
     * found the default form state will be returned.
     *
     * The difference to the method coerceToControlFormState is that null will be
     * handled as undefined and the defaultFormState is used
     *
     * @param controlId A control id
     * @param defaultFormState The default form state
     * @param builderFormState The form state provides as build state parameter
     * @param optionsFormState The form state set by the decorator in the form definition
     */
    coerceToGroupFormState(controlId, defaultFormState, builderFormState, optionsFormState) {
        let formState = defaultFormState;
        if (builderFormState && builderFormState[controlId]) {
            formState = builderFormState[controlId];
        }
        else if (optionsFormState) {
            formState = optionsFormState;
        }
        if (typeof formState === 'function') {
            formState = formState();
            if (!formState) {
                throw new Error(`The form state function for the form group '${controlId}' returns a empty value (null/undefined)`);
            }
            if (typeof formState !== 'object') {
                throw new Error(`The form state function for the form group '${controlId}' returns a non object like value instead '${typeof formState}'`);
            }
        }
        return formState;
    }
    /**
     * Coerce to the form state for the specified controlId. If none form state is
     * found the default form state will be returned.
     *
     * The difference to the method coerceToControlFormState is that null will be
     * handled as undefined and the defaultFormState is used
     *
     * @param controlId A control id
     * @param defaultFormState The default form state
     * @param builderFormState The form state provides as build state parameter
     * @param optionsFormState The form state set by the decorator in the form definition
     */
    coerceToArrayFormState(controlId, defaultFormState, builderFormState, optionsFormState) {
        let formState = defaultFormState;
        if (builderFormState && builderFormState[controlId] && Array.isArray(builderFormState[controlId])) {
            formState = builderFormState[controlId];
        }
        else if (optionsFormState && Array.isArray(optionsFormState)) {
            formState = optionsFormState;
        }
        if (typeof formState === 'function') {
            formState = formState();
            if (!formState) {
                throw new Error(`The form state function for the form array '${controlId}' returns a empty value (null/undefined)`);
            }
            if (!Array.isArray(formState)) {
                throw new Error(`The form state function for the form array '${controlId}' returns a non array like value instead '${typeof formState}'`);
            }
        }
        return formState;
    }
    extractControlChanges() {
        return (getMetadata(MetadataKeys.CONTROL_CHANGES, this.definition.prototype) ??
            new Map());
    }
    extractControlSetValue() {
        return (getMetadata(MetadataKeys.CONTROL_SET_VALUE, this.definition.prototype) ??
            new Map());
    }
    extractValidators() {
        return (getMetadata(MetadataKeys.CONTROL_VALIDATORS, this.definition.prototype) ??
            new Map());
    }
    extractAsyncValidators() {
        return (getMetadata(MetadataKeys.CONTROL_ASYNC_VALIDATORS, this.definition.prototype) ?? new Map());
    }
    extractArrayGroups() {
        return (getMetadata(MetadataKeys.FORM_ARRAY_GROUPS, this.definition.prototype) ??
            new Map());
    }
    extractArrayControls() {
        return (getMetadata(MetadataKeys.FORM_ARRAY_CONTROLS, this.definition.prototype) ?? new Map());
    }
    extractGroups() {
        return (getMetadata(MetadataKeys.FORM_GROUPS, this.definition.prototype) ??
            new Map());
    }
    extractControls() {
        return (getMetadata(MetadataKeys.FORM_CONTROLS, this.definition.prototype) ??
            new Map());
    }
    extractFormOptions() {
        const options = GetDefinitionMetadata(this.definition);
        if (!options || !options['id']) {
            throw new Error('Ensure that the @UseForm decorator is used.');
        }
        if (!options['providers']) {
            options['providers'] = [];
        }
        return options;
    }
}

class FormDirective extends FormGroupDirective {
    get submitting() {
        return this.submitting$.value;
    }
    get submitError() {
        return this.submitError$.value;
    }
    get loading() {
        return this.loading$.value;
    }
    get loaded() {
        return this.loaded$.value;
    }
    get loadingError() {
        return this.loadingError$.value;
    }
    set useFormDefinition(value) {
        if (value) {
            this._formDefinition = value;
            this.form = value.rxapFormGroup;
        }
    }
    get formDefinition() {
        return this._formDefinition;
    }
    constructor(cdr, formDefinition = null, 
    // skip self, bc the token is set to null
    submitMethod = null, 
    // skip self, bc the token is set to null
    loadMethod = null, 
    // skip self, bc the token is set to null
    loadFailedMethod = null, 
    // skip self, bc the token is set to null
    loadSuccessfulMethod = null, 
    // skip self, bc the token is set to null
    submitFailedMethod = null, 
    // skip self, bc the token is set to null
    submitSuccessfulMethod = null, 
    // skip self, bc the token is set to null
    formDefinitionBuilder = null, loadingIndicatorService = null) {
        super([], []);
        this.cdr = cdr;
        this.loadMethod = loadMethod;
        this.loadFailedMethod = loadFailedMethod;
        this.loadSuccessfulMethod = loadSuccessfulMethod;
        this.submitFailedMethod = submitFailedMethod;
        this.submitSuccessfulMethod = submitSuccessfulMethod;
        this.formDefinitionBuilder = formDefinitionBuilder;
        this.loadingIndicatorService = loadingIndicatorService;
        /**
         * Emits when the submit method is executed without errors. The result of the
         * submit method is passed as event object.
         *
         * If no submit method is defined then emit after the submit button
         * is clicked.
         */
        this.rxapSubmit = new EventEmitter();
        this.invalidSubmit = new EventEmitter();
        // eslint-disable-next-line @angular-eslint/no-output-rename
        this.submitSuccessful$ = new EventEmitter();
        this.submitting$ = new ToggleSubject();
        this.submitError$ = new BehaviorSubject(null);
        this.loading$ = new ToggleSubject();
        this.loaded$ = new ToggleSubject();
        this.loadingError$ = new BehaviorSubject(null);
        this.submitMethod = null;
        this._autoSubmitSubscription = new Subscription();
        if (submitMethod) {
            this.submitMethod = submitMethod;
        }
        if (!formDefinition && formDefinitionBuilder) {
            formDefinition = formDefinitionBuilder.build();
        }
        if (formDefinition) {
            this._formDefinition = formDefinition;
            this.form = formDefinition.rxapFormGroup;
        }
        this.loadingIndicatorService?.attachLoading(this.loading$);
        this.loadingIndicatorService?.attachLoading(this.submitting$);
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        const formChange = changes['form'];
        if (formChange && !formChange.firstChange) {
            this.loadInitialState(formChange.currentValue);
        }
    }
    ngOnInit() {
        if (!this._formDefinition) {
            // TODO : replace with rxap error
            throw new Error('The form definition instance is not defined');
        }
        if (!this.form) {
            // TODO : replace with rxap error
            throw new Error('The form instance is not defined');
        }
        this.loadInitialState(this.form);
        function HasNgOnInitMethod(obj) {
            return obj && typeof obj.ngOnInit === 'function';
        }
        if (HasNgOnInitMethod(this._formDefinition)) {
            this._formDefinition.ngOnInit();
        }
        if (this._formDefinition.rxapMetadata.autoSubmit) {
            const debounce = typeof this._formDefinition.rxapMetadata.autoSubmit === 'number'
                ? this._formDefinition.rxapMetadata.autoSubmit
                : 5000;
            this._autoSubmitSubscription = this
                .form
                .valueChanges
                .pipe(debounceTime(debounce), filter(() => this.form.valid), tap((value) => {
                if (isDevMode()) {
                    console.debug(`Auto submit form '${this._formDefinition.rxapMetadata.id}'`, value);
                }
            }), tap(() => this.submit()))
                .subscribe();
        }
    }
    onSubmit($event) {
        $event.preventDefault();
        super.onSubmit($event);
        if (this.form.valid) {
            this.submit();
        }
        else {
            // eslint-disable-next-line no-inner-declarations
            function reduceErrors(control, errors = {}) {
                if (control.invalid) {
                    if (control.controls) {
                        if (Array.isArray(control.controls)) {
                            const errorList = [];
                            for (const item of control.controls) {
                                errorList.push(reduceErrors(item));
                            }
                            errors[control.controlId] = errorList;
                        }
                        else {
                            const innerErrors = {};
                            for (const child of Object.values(control.controls)) {
                                reduceErrors(child, innerErrors);
                            }
                            errors[control.controlId] = innerErrors;
                        }
                    }
                    else {
                        if (control.errors) {
                            errors[control.controlId] = control.errors;
                        }
                    }
                }
                return errors;
            }
            if (isDevMode()) {
                console.log('Form submit is not valid for: ' + this.form.controlId, this.form.errors);
                // eslint-disable-next-line no-inner-declarations
                function printErrorControls(control) {
                    if (!control.valid) {
                        console.group(control.controlId);
                        if (control.controls) {
                            if (Array.isArray(control.controls)) {
                                for (let i = 0; i < control.controls.length; i++) {
                                    const child = control.controls[i];
                                    if (!child.valid) {
                                        console.group(`index: ${i}`);
                                        printErrorControls(child);
                                        console.groupEnd();
                                    }
                                }
                            }
                            else {
                                for (const child of Object.values(control.controls)) {
                                    printErrorControls(child);
                                }
                            }
                        }
                        else {
                            if (control.errors) {
                                for (const [key, value] of Object.entries(control.errors)) {
                                    console.group(key);
                                    console.log(value);
                                    console.groupEnd();
                                }
                            }
                            console.log('value: ', control.value);
                        }
                        console.groupEnd();
                    }
                }
                printErrorControls(this.form);
            }
            this.invalidSubmit.emit(reduceErrors(this.form));
        }
        return false;
    }
    loadInitialState(form) {
        if (this.initial) {
            if (isDevMode()) {
                console.log('use the value from input initial');
            }
            form.patchValue(this.initial);
        }
        else {
            if (this.loadMethod) {
                this.loading$.enable();
                try {
                    const resultOrPromise = this.loadMethod.call();
                    if (isPromise(resultOrPromise)) {
                        resultOrPromise
                            .then((value) => {
                            form.patchValue(value);
                            this.loaded$.enable();
                            this.loadSuccessful(value);
                        })
                            .catch((error) => {
                            this.loadingError$.next(error);
                            this.loadFailed(error);
                        })
                            .finally(() => {
                            this.loading$.disable();
                            this.cdr.detectChanges();
                        });
                    }
                    else if (isObject(resultOrPromise)) {
                        form.patchValue(resultOrPromise);
                        this.loaded$.enable();
                        this.loadSuccessful(resultOrPromise);
                        this.loading$.disable();
                    }
                }
                catch (error) {
                    this.loaded$.disable();
                    this.loadingError$.next(error);
                    this.loadFailed(error);
                    this.loading$.disable();
                }
            }
            else {
                if (isDevMode()) {
                    console.warn('The form loading method is not defined for: ' + this.form.controlId);
                }
                this.loaded$.enable();
            }
        }
    }
    loadSuccessful(value) {
        if (this.loadSuccessfulMethod) {
            this.loadSuccessfulMethod.call(value);
        }
        else if (isDevMode()) {
            console.warn('The load successful is not defined for: ' + this.form.controlId);
        }
    }
    loadFailed(error) {
        console.debug('Load Error:', error);
        console.error('Load Error:', error.message);
        if (this.loadFailedMethod) {
            this.loadFailedMethod?.call(error);
        }
        else if (isDevMode()) {
            console.warn('The form loading failed for: ' + this.form.controlId);
        }
    }
    getSubmitValue() {
        let value = undefined;
        if (typeof this._formDefinition['getSubmitValue'] === 'function') {
            value = this._formDefinition.getSubmitValue();
        }
        else if (typeof this._formDefinition['toJSON'] === 'function') {
            value = this._formDefinition.toJSON();
        }
        value = value ?? this.form.value;
        return clone(value);
    }
    submit() {
        const value = this.getSubmitValue();
        if (this.submitMethod) {
            Reflect.set(this, 'submitted', false);
            this.submitting$.enable();
            this.submitError$.next(null);
            try {
                const resultOrPromise = this.submitMethod.call(value);
                if (isPromise(resultOrPromise)) {
                    resultOrPromise
                        .then((result) => {
                        Reflect.set(this, 'submitted', true);
                        this.rxapSubmit.emit(result);
                        this.submitSuccessful(result);
                    })
                        .catch((error) => {
                        this.submitError$.next(error);
                        this.submitFailed(error);
                    })
                        .finally(() => {
                        this.submitting$.disable();
                        this.cdr.detectChanges();
                    });
                }
                else {
                    Reflect.set(this, 'submitted', true);
                    this.rxapSubmit.emit(resultOrPromise);
                    this.submitSuccessful(resultOrPromise);
                    this.submitting$.disable();
                }
            }
            catch (error) {
                this.submitting$.disable();
                this.submitError$.next(error);
                this.submitFailed(error);
            }
        }
        else {
            if (isDevMode()) {
                console.warn('The form submit method is not defined for: ' + this.form.controlId);
            }
            this.rxapSubmit.emit(value);
            this.submitSuccessful(value);
        }
    }
    submitFailed(error) {
        console.debug('Submit Error:', error);
        console.error('Submit Error:', error.message);
        if (this.submitFailedMethod) {
            this.submitFailedMethod.call(error);
        }
        else if (isDevMode()) {
            console.warn('The form submit failed method is not defined for: ' +
                this.form.controlId);
        }
    }
    submitSuccessful(value) {
        this.submitSuccessful$.next(value);
        if (this.submitSuccessfulMethod) {
            this.submitSuccessfulMethod.call(value);
        }
        else if (isDevMode()) {
            console.warn('The form submit successful method is not defined for: ' +
                this.form.controlId);
        }
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this._autoSubmitSubscription?.unsubscribe();
        function HasNgOnDestroyMethod(obj) {
            return obj && typeof obj.ngOnDestroy === 'function';
        }
        if (HasNgOnDestroyMethod(this._formDefinition)) {
            this._formDefinition.ngOnDestroy();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormDirective, deps: [{ token: ChangeDetectorRef }, { token: RXAP_FORM_DEFINITION, optional: true }, { token: RXAP_FORM_SUBMIT_METHOD, optional: true, skipSelf: true }, { token: RXAP_FORM_LOAD_METHOD, optional: true, skipSelf: true }, { token: RXAP_FORM_LOAD_FAILED_METHOD, optional: true, skipSelf: true }, { token: RXAP_FORM_LOAD_SUCCESSFUL_METHOD, optional: true, skipSelf: true }, { token: RXAP_FORM_SUBMIT_FAILED_METHOD, optional: true, skipSelf: true }, { token: RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD, optional: true, skipSelf: true }, { token: RXAP_FORM_DEFINITION_BUILDER, optional: true, skipSelf: true }, { token: LoadingIndicatorService, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormDirective, isStandalone: true, selector: "form[rxapForm]:not([formGroup]):not([ngForm]),rxap-form,form[rxapForm]", inputs: { initial: "initial", useFormDefinition: ["rxapForm", "useFormDefinition"], submitMethod: "submitMethod" }, outputs: { ngSubmit: "ngSubmit", rxapSubmit: "rxapSubmit", invalidSubmit: "invalidSubmit", submitSuccessful$: "submitSuccessful" }, host: { listeners: { "reset": "onReset()" }, properties: { "class.rxap-submitting": "this.submitting", "class.rxap-submit-error": "this.submitError", "class.rxap-loading": "this.loading", "class.rxap-loaded": "this.loaded", "class.rxap-loading-error": "this.loadingError" } }, providers: [
            {
                provide: ControlContainer,
                // ignore coverage
                useExisting: forwardRef(() => FormDirective),
            },
            // region form provider clear
            // form provider that are directly associated with the current form
            // are cleared to prevent that inner forms can access this providers
            // Example: The parent form has a submit method provider and the inner should
            // not have a submit method provider. If the parent submit method provider is
            // not cleared then the inner form uses the parent submit method provider on
            // submit
            {
                provide: RXAP_FORM_SUBMIT_METHOD,
                useValue: null,
            },
            {
                provide: RXAP_FORM_LOAD_METHOD,
                useValue: null,
            },
            {
                provide: RXAP_FORM_LOAD_FAILED_METHOD,
                useValue: null,
            },
            {
                provide: RXAP_FORM_LOAD_SUCCESSFUL_METHOD,
                useValue: null,
            },
            {
                provide: RXAP_FORM_SUBMIT_FAILED_METHOD,
                useValue: null,
            },
            {
                provide: RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD,
                useValue: null,
            },
            {
                provide: RXAP_FORM_DEFINITION_BUILDER,
                useValue: null,
            },
            // endregion
        ], exportAs: ["rxapForm"], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'form[rxapForm]:not([formGroup]):not([ngForm]),rxap-form,form[rxapForm]',
                    providers: [
                        {
                            provide: ControlContainer,
                            // ignore coverage
                            useExisting: forwardRef(() => FormDirective),
                        },
                        // region form provider clear
                        // form provider that are directly associated with the current form
                        // are cleared to prevent that inner forms can access this providers
                        // Example: The parent form has a submit method provider and the inner should
                        // not have a submit method provider. If the parent submit method provider is
                        // not cleared then the inner form uses the parent submit method provider on
                        // submit
                        {
                            provide: RXAP_FORM_SUBMIT_METHOD,
                            useValue: null,
                        },
                        {
                            provide: RXAP_FORM_LOAD_METHOD,
                            useValue: null,
                        },
                        {
                            provide: RXAP_FORM_LOAD_FAILED_METHOD,
                            useValue: null,
                        },
                        {
                            provide: RXAP_FORM_LOAD_SUCCESSFUL_METHOD,
                            useValue: null,
                        },
                        {
                            provide: RXAP_FORM_SUBMIT_FAILED_METHOD,
                            useValue: null,
                        },
                        {
                            provide: RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD,
                            useValue: null,
                        },
                        {
                            provide: RXAP_FORM_DEFINITION_BUILDER,
                            useValue: null,
                        },
                        // endregion
                    ],
                    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
                    host: { '(reset)': 'onReset()' },
                    // eslint-disable-next-line @angular-eslint/no-outputs-metadata-property
                    outputs: ['ngSubmit'],
                    exportAs: 'rxapForm',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef, decorators: [{
                    type: Inject,
                    args: [ChangeDetectorRef]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_DEFINITION]
                }] }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_SUBMIT_METHOD]
                }] }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_LOAD_METHOD]
                }] }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_LOAD_FAILED_METHOD]
                }] }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_LOAD_SUCCESSFUL_METHOD]
                }] }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_SUBMIT_FAILED_METHOD]
                }] }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD]
                }] }, { type: RxapFormBuilder, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [RXAP_FORM_DEFINITION_BUILDER]
                }] }, { type: i2.LoadingIndicatorService, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [LoadingIndicatorService]
                }] }], propDecorators: { initial: [{
                type: Input
            }], rxapSubmit: [{
                type: Output
            }], invalidSubmit: [{
                type: Output
            }], submitSuccessful$: [{
                type: Output,
                args: ['submitSuccessful']
            }], submitting: [{
                type: HostBinding,
                args: ['class.rxap-submitting']
            }], submitError: [{
                type: HostBinding,
                args: ['class.rxap-submit-error']
            }], loading: [{
                type: HostBinding,
                args: ['class.rxap-loading']
            }], loaded: [{
                type: HostBinding,
                args: ['class.rxap-loaded']
            }], loadingError: [{
                type: HostBinding,
                args: ['class.rxap-loading-error']
            }], useFormDefinition: [{
                type: Input,
                args: ['rxapForm']
            }], submitMethod: [{
                type: Input
            }] } });

class FormLoadedDirective {
    constructor(formDirective, template, viewContainerRef) {
        this.formDirective = formDirective;
        this.template = template;
        this.viewContainerRef = viewContainerRef;
    }
    ngAfterViewInit() {
        this.subscription = this.formDirective.loaded$.pipe(distinctUntilChanged(), tap(loading => {
            this.viewContainerRef.clear();
            if (loading) {
                this.viewContainerRef.createEmbeddedView(this.template);
            }
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormLoadedDirective, deps: [{ token: FormDirective }, { token: TemplateRef }, { token: ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormLoadedDirective, isStandalone: true, selector: "[rxapFormLoaded]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormLoadedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormLoaded]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i0.TemplateRef, decorators: [{
                    type: Inject,
                    args: [TemplateRef]
                }] }, { type: i0.ViewContainerRef, decorators: [{
                    type: Inject,
                    args: [ViewContainerRef]
                }] }] });

class FormLoadingErrorDirective {
    constructor(formDirective, template, viewContainerRef) {
        this.formDirective = formDirective;
        this.template = template;
        this.viewContainerRef = viewContainerRef;
    }
    ngAfterViewInit() {
        this.subscription = this.formDirective.loadingError$.pipe(tap(error => {
            this.viewContainerRef.clear();
            if (error !== null) {
                this.viewContainerRef.createEmbeddedView(this.template, { $implicit: error });
            }
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormLoadingErrorDirective, deps: [{ token: FormDirective }, { token: TemplateRef }, { token: ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormLoadingErrorDirective, isStandalone: true, selector: "[rxapFormLoadingError]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormLoadingErrorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormLoadingError]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i0.TemplateRef, decorators: [{
                    type: Inject,
                    args: [TemplateRef]
                }] }, { type: i0.ViewContainerRef, decorators: [{
                    type: Inject,
                    args: [ViewContainerRef]
                }] }] });

class FormLoadingDirective {
    constructor(formDirective, template, viewContainerRef) {
        this.formDirective = formDirective;
        this.template = template;
        this.viewContainerRef = viewContainerRef;
    }
    ngAfterViewInit() {
        this.subscription = this.formDirective.loading$.pipe(distinctUntilChanged(), tap(loading => {
            this.viewContainerRef.clear();
            if (loading) {
                this.viewContainerRef.createEmbeddedView(this.template);
            }
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormLoadingDirective, deps: [{ token: FormDirective }, { token: TemplateRef }, { token: ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormLoadingDirective, isStandalone: true, selector: "[rxapFormLoading]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormLoadingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormLoading]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i0.TemplateRef, decorators: [{
                    type: Inject,
                    args: [TemplateRef]
                }] }, { type: i0.ViewContainerRef, decorators: [{
                    type: Inject,
                    args: [ViewContainerRef]
                }] }] });

class FormSubmitFailedDirective {
    constructor(formDirective, template, viewContainerRef) {
        this.formDirective = formDirective;
        this.template = template;
        this.viewContainerRef = viewContainerRef;
    }
    ngAfterViewInit() {
        this.subscription = this.formDirective.submitError$.pipe(tap(error => {
            this.viewContainerRef.clear();
            if (error !== null) {
                this.viewContainerRef.createEmbeddedView(this.template, { $implicit: error });
            }
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitFailedDirective, deps: [{ token: FormDirective }, { token: TemplateRef }, { token: ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormSubmitFailedDirective, isStandalone: true, selector: "[rxapFormSubmitFailed]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitFailedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormSubmitFailed]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i0.TemplateRef, decorators: [{
                    type: Inject,
                    args: [TemplateRef]
                }] }, { type: i0.ViewContainerRef, decorators: [{
                    type: Inject,
                    args: [ViewContainerRef]
                }] }] });

class FormSubmitInvalidDirective {
    constructor(formDirective, template, viewContainerRef) {
        this.formDirective = formDirective;
        this.template = template;
        this.viewContainerRef = viewContainerRef;
    }
    ngAfterViewInit() {
        this.subscription = this.formDirective.invalidSubmit.pipe(tap(result => {
            this.viewContainerRef.clear();
            this.viewContainerRef.createEmbeddedView(this.template, { $implicit: result });
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitInvalidDirective, deps: [{ token: FormDirective }, { token: TemplateRef }, { token: ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormSubmitInvalidDirective, isStandalone: true, selector: "[rxapFormSubmitInvalid]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitInvalidDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormSubmitInvalid]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i0.TemplateRef, decorators: [{
                    type: Inject,
                    args: [TemplateRef]
                }] }, { type: i0.ViewContainerRef, decorators: [{
                    type: Inject,
                    args: [ViewContainerRef]
                }] }] });

class FormSubmitSuccessfulDirective {
    constructor(formDirective, template, viewContainerRef) {
        this.formDirective = formDirective;
        this.template = template;
        this.viewContainerRef = viewContainerRef;
    }
    ngAfterViewInit() {
        this.subscription = this.formDirective.submitSuccessful$.pipe(tap(result => {
            this.viewContainerRef.clear();
            this.viewContainerRef.createEmbeddedView(this.template, { $implicit: result });
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitSuccessfulDirective, deps: [{ token: FormDirective }, { token: TemplateRef }, { token: ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormSubmitSuccessfulDirective, isStandalone: true, selector: "[rxapFormSubmitSuccessful]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitSuccessfulDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormSubmitSuccessful]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i0.TemplateRef, decorators: [{
                    type: Inject,
                    args: [TemplateRef]
                }] }, { type: i0.ViewContainerRef, decorators: [{
                    type: Inject,
                    args: [ViewContainerRef]
                }] }] });

class FormSubmitDirective extends ConfirmClick {
    set resetAfterSubmit(value) {
        this._resetAfterSubmit = coerceBoolean(value);
    }
    constructor(formDirective, router = null) {
        super();
        this.formDirective = formDirective;
        this.router = router;
        this.type = 'button';
        this.afterSubmit = new EventEmitter();
        this._resetAfterSubmit = false;
    }
    execute() {
        this.formDirective.form.markAllAsDirty();
        this.formDirective.form.markAllAsTouched();
        this.formDirective.cdr.markForCheck();
        this.subscription = this.formDirective.rxapSubmit.pipe(take(1), tap(value => {
            const clonedValue = clone(value);
            this.afterSubmit.emit(clonedValue);
            if (this._resetAfterSubmit) {
                this.formDirective.reset();
            }
            if (this.navigateAfterSubmit) {
                if (!this.router) {
                    throw new Error('Could not resolve the router!');
                }
                return this.router.navigate(this.navigateAfterSubmit);
            }
            return Promise.resolve();
        })).subscribe();
        this.formDirective.onSubmit(new Event('submit'));
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitDirective, deps: [{ token: FormDirective }, { token: Router, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormSubmitDirective, isStandalone: true, selector: "[rxapFormSubmit]", inputs: { type: "type", resetAfterSubmit: "resetAfterSubmit", navigateAfterSubmit: "navigateAfterSubmit" }, outputs: { afterSubmit: "afterSubmit" }, host: { listeners: { "click": "onClick()", "confirmed": "onConfirm()" }, properties: { "type": "this.type" } }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmitDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormSubmit]',
                    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
                    host: {
                        '(click)': 'onClick()',
                        '(confirmed)': 'onConfirm()',
                    },
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i2$1.Router, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [Router]
                }] }], propDecorators: { type: [{
                type: HostBinding,
                args: ['type']
            }, {
                type: Input
            }], resetAfterSubmit: [{
                type: Input
            }], navigateAfterSubmit: [{
                type: Input
            }], afterSubmit: [{
                type: Output
            }] } });

class FormSubmittingDirective {
    constructor(formDirective, template, viewContainerRef) {
        this.formDirective = formDirective;
        this.template = template;
        this.viewContainerRef = viewContainerRef;
    }
    ngAfterViewInit() {
        this.subscription = this.formDirective.submitting$.pipe(distinctUntilChanged(), tap(loading => {
            this.viewContainerRef.clear();
            if (loading) {
                this.viewContainerRef.createEmbeddedView(this.template);
            }
        })).subscribe();
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmittingDirective, deps: [{ token: FormDirective }, { token: TemplateRef }, { token: ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: FormSubmittingDirective, isStandalone: true, selector: "[rxapFormSubmitting]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: FormSubmittingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rxapFormSubmitting]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FormDirective, decorators: [{
                    type: Inject,
                    args: [FormDirective]
                }] }, { type: i0.TemplateRef, decorators: [{
                    type: Inject,
                    args: [TemplateRef]
                }] }, { type: i0.ViewContainerRef, decorators: [{
                    type: Inject,
                    args: [ViewContainerRef]
                }] }] });

class ParentControlContainerDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: ParentControlContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.0.1", type: ParentControlContainerDirective, isStandalone: true, selector: "[formControlName][parentControlContainer],[formControlGroup][parentControlContainer]", providers: [
            {
                provide: ControlContainer,
                useExisting: ControlContainer,
            },
        ], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: ParentControlContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[formControlName][parentControlContainer],[formControlGroup][parentControlContainer]',
                    providers: [
                        {
                            provide: ControlContainer,
                            useExisting: ControlContainer,
                        },
                    ],
                    standalone: true,
                }]
        }] });

class RxapFormsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: RxapFormsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.1", ngImport: i0, type: RxapFormsModule, imports: [FormDirective,
            FormSubmitFailedDirective,
            FormLoadingDirective,
            FormSubmittingDirective,
            FormLoadingErrorDirective,
            FormLoadedDirective,
            FormControlErrorDirective,
            FormControlMarkDirtyDirective,
            FormControlMarkTouchedDirective,
            FormControlMarkUntouchedDirective,
            FormControlMarkPristineDirective,
            FormSubmitDirective,
            FormGroupNameDirective,
            FormControlNameDirective,
            ParentControlContainerDirective,
            FormSubmitSuccessfulDirective,
            FormSubmitInvalidDirective], exports: [FormDirective,
            FormSubmitFailedDirective,
            FormLoadingDirective,
            FormSubmittingDirective,
            FormLoadingErrorDirective,
            FormLoadedDirective,
            FormControlErrorDirective,
            FormControlMarkDirtyDirective,
            FormControlMarkTouchedDirective,
            FormControlMarkUntouchedDirective,
            FormControlMarkPristineDirective,
            FormSubmitDirective,
            FormGroupNameDirective,
            FormControlNameDirective,
            ParentControlContainerDirective,
            FormSubmitSuccessfulDirective,
            FormSubmitInvalidDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: RxapFormsModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: RxapFormsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        FormDirective,
                        FormSubmitFailedDirective,
                        FormLoadingDirective,
                        FormSubmittingDirective,
                        FormLoadingErrorDirective,
                        FormLoadedDirective,
                        FormControlErrorDirective,
                        FormControlMarkDirtyDirective,
                        FormControlMarkTouchedDirective,
                        FormControlMarkUntouchedDirective,
                        FormControlMarkPristineDirective,
                        FormSubmitDirective,
                        FormGroupNameDirective,
                        FormControlNameDirective,
                        ParentControlContainerDirective,
                        FormSubmitSuccessfulDirective,
                        FormSubmitInvalidDirective,
                    ],
                    exports: [
                        FormDirective,
                        FormSubmitFailedDirective,
                        FormLoadingDirective,
                        FormSubmittingDirective,
                        FormLoadingErrorDirective,
                        FormLoadedDirective,
                        FormControlErrorDirective,
                        FormControlMarkDirtyDirective,
                        FormControlMarkTouchedDirective,
                        FormControlMarkUntouchedDirective,
                        FormControlMarkPristineDirective,
                        FormSubmitDirective,
                        FormGroupNameDirective,
                        FormControlNameDirective,
                        ParentControlContainerDirective,
                        FormSubmitSuccessfulDirective,
                        FormSubmitInvalidDirective,
                    ],
                }]
        }] });

function ToFormMethod(call) {
    return { call };
}
/**
 * @deprecated use ToFormMethod instead
 *
 * @param call the call method implementation
 */
function ToFormSubmitMethod(call) {
    return ToFormMethod(call);
}

function ControlAsyncValidator(...controlIds) {
    return function (target, propertyKey) {
        for (const controlId of controlIds) {
            setMetadataMapSet(controlId, propertyKey, MetadataKeys.CONTROL_ASYNC_VALIDATORS, target);
        }
    };
}

function ControlChange(...controlIds) {
    return function (target, propertyKey) {
        for (const controlId of controlIds) {
            setMetadataMapSet(controlId, propertyKey, MetadataKeys.CONTROL_CHANGES, target);
        }
    };
}

function ControlSetValue(controlId, options = {}) {
    return function (target, propertyKey) {
        setMetadataMapSet(controlId, {
            ...options,
            propertyKey,
        }, MetadataKeys.CONTROL_SET_VALUE, target);
    };
}

function ControlValidator(...controlIds) {
    return function (target, propertyKey) {
        for (const controlId of controlIds) {
            setMetadataMapSet(controlId, propertyKey, MetadataKeys.CONTROL_VALIDATORS, target);
        }
    };
}

function RxapForm(optionsOrId, className = 'FormDefinition', packageName = '@rxap/forms') {
    return function (target) {
        DefinitionMetadata(optionsOrId, className, packageName)(target);
    };
}

function UseFormArrayGroup(definition, options = {}) {
    return function (target, propertyKey) {
        setMetadataMap(propertyKey, {
            ...options,
            definition,
        }, MetadataKeys.FORM_ARRAY_GROUPS, target);
    };
}
function UseFormArrayControl(options = {}) {
    return function (target, propertyKey) {
        setMetadataMap(propertyKey, options, MetadataKeys.FORM_ARRAY_CONTROLS, target);
    };
}

function UseFormControl(options = {}) {
    return function (target, propertyKey) {
        setMetadataMap(propertyKey, options, MetadataKeys.FORM_CONTROLS, target);
    };
}

function UseFormGroup(definition, options = {}) {
    return function (target, propertyKey) {
        setMetadataMap(propertyKey, {
            ...options,
            definition,
        }, MetadataKeys.FORM_GROUPS, target);
    };
}

class ControlValueAccessor {
    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = (value) => {
        };
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onTouched = () => {
        };
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.setDisabledState = (isDisabled) => {
        };
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
}

const RXAP_FORM_INITIAL_STATE = new InjectionToken('rxap/forms/initial-state');
const RXAP_FORM_CONTEXT = new InjectionToken('rxap/forms/context');

// region validators
// endregion

/**
 * Generated bundle index. Do not edit.
 */

export { ControlAsyncValidator, ControlChange, ControlSetValue, ControlValidator, ControlValueAccessor, FormArrayControlManager, FormControlErrorDirective, FormControlMarkDirtyDirective, FormControlMarkPristineDirective, FormControlMarkTouchedDirective, FormControlMarkUntouchedDirective, FormControlNameDirective, FormDirective, FormGroupNameDirective, FormLoadedDirective, FormLoadingDirective, FormLoadingErrorDirective, FormSubmitDirective, FormSubmitFailedDirective, FormSubmitInvalidDirective, FormSubmitSuccessfulDirective, FormSubmittingDirective, IsArray, IsBoolean, IsComplex, IsDate, IsEmail, IsEnum, IsIP, IsInt, IsNumber, IsObject, IsPhoneNumber, IsPort, IsString, IsUUID, IsUrl, MetadataKeys, ParentControlContainerDirective, RXAP_FORM_CONTEXT, RXAP_FORM_DEFINITION, RXAP_FORM_DEFINITION_BUILDER, RXAP_FORM_INITIAL_STATE, RXAP_FORM_LOAD_FAILED_METHOD, RXAP_FORM_LOAD_METHOD, RXAP_FORM_LOAD_SUCCESSFUL_METHOD, RXAP_FORM_SUBMIT_FAILED_METHOD, RXAP_FORM_SUBMIT_METHOD, RXAP_FORM_SUBMIT_SUCCESSFUL_METHOD, RxapForm, RxapFormArray, RxapFormBuilder, RxapFormControl, RxapFormGroup, RxapFormsModule, RxapValidators, ToFormMethod, ToFormSubmitMethod, UseFormArrayControl, UseFormArrayGroup, UseFormControl, UseFormGroup, _IsNumber, controlDisabled$, controlDisabledWhile, controlEnabled$, controlEnabledWhile, controlErrorChanges$, controlReadonly$, controlStatusChanges$, controlValueChanges$, disableControl, enableControl, hasErrorAndDirty, hasErrorAndTouched, isNumber, markAllDirty, markAllPristine, markAllUntouched, mergeControlValidators, selectControlValue$, validateControlOn };
